import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { blurImageUrl } from '../TransferCustomizationDrawer/utils';
import CustomWallpaperToolsExperiment from '@wetransfer/transfer/constant/experiments/custom-wallpaper-tools-experiment';
import {
  associateWallpaperWithTransfer,
  deleteWallpaper,
} from '@wetransfer/transfer/customWallpaperApi';
import Helper from '@wetransfer/transfer/helper';
import {
  displayWallpaper,
  resetWallpaper,
} from '@wetransfer/transfer/helper/wallpaper';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import { useExperiments } from '@wetransfer/transfer/modules/experiments/hooks';
import {
  Wallpaper,
  clearCustomWallpaper as clearCustomWallpaperAction,
  selectCustomWallpaper,
  setCustomWallpaper as setCustomWallpaperAction,
} from '@wetransfer/transfer/store/customWallpapers';
import {
  selectAccountTitle,
  selectAccountWallpaperLink,
} from '@wetransfer/transfer/store/selector/account';
import {
  selectTransferId,
  selectTransferState,
} from '@wetransfer/transfer/store/selector/transfer';
import { State } from '@wetransfer/transfer/store/types';
import { TRANSFER_STATES } from '@wetransfer/transfer/transfer-bundle/constant/transfer-states';

import { GENERIC_ERROR_MESSAGE, INVALID_FILE_ERROR_MESSAGE } from './constants';
import { TransferCustomizationContext } from './context';
import { TransferCustomizationContextValue } from './context/types';
import { selectNormalizedWallpapers } from './store/selectors';
import { WallpaperCategory } from './types';
import {
  deleteWallpapers,
  uploadBrandedWallpaper,
  uploadCuratedWallpaper,
  uploadCustomWallpaperFile,
  getWallpaperSrc,
  pollUntil,
  getWallpaperPreviews,
  validateWallpaperFile,
} from './utils';

export const TransferCustomizationProvider: React.FC = ({ children }) => {
  const transferState = useSelector(selectTransferState);
  const wallpaperTitle = useSelector(selectAccountTitle);
  const wallpaperLink = useSelector(selectAccountWallpaperLink);

  const existingWallpapers: Wallpaper[] = useSelector(
    selectNormalizedWallpapers
  );
  const selectedWallpaper = useSelector(selectCustomWallpaper);

  const dispatch = useDispatch();

  const setSelectedWallpaper = useCallback(
    (wallpaper: Wallpaper) => {
      dispatch(setCustomWallpaperAction(wallpaper));
    },
    [dispatch]
  );

  const clearSelectedWallpaper = useCallback(() => {
    dispatch(clearCustomWallpaperAction());
    resetWallpaper();
  }, [dispatch]);

  const isEnabledForSender =
    useExperiments().isReady &&
    CustomWallpaperToolsExperiment.isShowCustomWallpaperTools() &&
    !Browser.isMobile;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [uploadedCustomWallpaper, setUploadedCustomWallpaper] =
    useState<Wallpaper | null>(null);

  const [pendingWallpaperIds, setPendingWallpaperIds] = useState(
    new Set<string>()
  );

  const addPendingWallpaperId = useCallback(
    (localId: string) =>
      setPendingWallpaperIds((set) => new Set(set.add(localId))),
    []
  );

  const deletePendingWallpaperId = useCallback(
    (key: string) =>
      setPendingWallpaperIds((set) => {
        set.delete(key);

        return new Set(set);
      }),
    []
  );

  const clearPendingWallpaperIds = useCallback(
    () => setPendingWallpaperIds(new Set()),
    []
  );

  const [completedWallpaperAssetIds, setCompletedWallpaperAssetIds] = useState(
    new Map<string, string>()
  );

  const addCompletedWallpaperAssetId = useCallback(
    (localId: string, assetId) =>
      setCompletedWallpaperAssetIds(
        (map) => new Map(map.set(localId, assetId))
      ),
    []
  );

  const clearCompletedWallpaperAssetIds = useCallback(
    () => setCompletedWallpaperAssetIds(new Map()),
    []
  );

  const wallpapers = useMemo(() => {
    if (uploadedCustomWallpaper) {
      return [uploadedCustomWallpaper, ...existingWallpapers];
    }

    return existingWallpapers;
  }, [uploadedCustomWallpaper, existingWallpapers]);

  const isUploadInProgress =
    uploadedCustomWallpaper &&
    pendingWallpaperIds.has(uploadedCustomWallpaper.localId);

  const transferId = useSelector(selectTransferId);

  // When both wallpaper creation is complete and transfer has started (so it has a transferId),
  // associate the wallpaper with transfer
  useEffect(() => {
    if (
      isEnabledForSender &&
      selectedWallpaper &&
      transferId &&
      transferState === TRANSFER_STATES.FINISHED &&
      completedWallpaperAssetIds.has(selectedWallpaper.localId)
    ) {
      // Clear selected wallpaper so this effect doesn't run again
      clearSelectedWallpaper();

      const assetId = completedWallpaperAssetIds.get(
        selectedWallpaper?.localId
      );

      const associate = async () => {
        let isAssociated = false;

        try {
          await pollUntil(
            async () => {
              try {
                const status = await associateWallpaperWithTransfer(
                  assetId,
                  transferId
                );

                if (status === 200) {
                  return true;
                }
              } catch {
                // Try again
              }

              return false;
            },
            200,
            5
          );

          isAssociated = true;
        } catch (error) {
          // Since the transfer may have already succeeded, it would be confusing to show an error message.
          // Fail silently for now
          clearCompletedWallpaperAssetIds();
          clearPendingWallpaperIds();
        }

        // Clean up
        clearSelectedWallpaper();
        clearCompletedWallpaperAssetIds();
        clearPendingWallpaperIds();
        setUploadedCustomWallpaper(null);

        // Delete extra wallpapers that were created
        const wallpaperIds = Array.from(completedWallpaperAssetIds.keys());

        try {
          await deleteWallpapers(
            (isAssociated
              ? wallpaperIds.filter(
                  (localId) => localId !== selectedWallpaper?.localId
                )
              : wallpaperIds
            ).map((localId) => completedWallpaperAssetIds.get(localId))
          );
        } catch {
          // User doesn't need to know about this, but we should log it
          throw new Error('Failed to delete one or more unused wallpapers');
        }
      };
      associate();
    }
  }, [
    isEnabledForSender,
    transferId,
    transferState,
    uploadedCustomWallpaper,
    clearCompletedWallpaperAssetIds,
    clearPendingWallpaperIds,
    completedWallpaperAssetIds,
    selectedWallpaper?.localId,
    setSelectedWallpaper,
    wallpapers,
    clearSelectedWallpaper,
    selectedWallpaper,
  ]);

  // In the off chance the user deletes the selected branded wallpaper before transfer is sent, clear selected wallpaper
  useEffect(() => {
    if (
      isEnabledForSender &&
      transferState === TRANSFER_STATES.INIT &&
      selectedWallpaper?.localId &&
      !pendingWallpaperIds.has(selectedWallpaper?.localId) &&
      !wallpapers.find(({ localId }) => localId === selectedWallpaper?.localId)
    ) {
      clearSelectedWallpaper();
    }
  }, [
    isEnabledForSender,
    transferState,
    wallpapers,
    selectedWallpaper?.localId,
    pendingWallpaperIds,
    clearSelectedWallpaper,
  ]);

  /**
   * Context methods
   */
  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);

  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const clearUploadedWallpaper = useCallback(async () => {
    clearSelectedWallpaper();
    setUploadedCustomWallpaper(null);

    try {
      if (uploadedCustomWallpaper?.assetId) {
        deleteWallpaper(uploadedCustomWallpaper.assetId);
      }
    } catch {
      // It's okay if this fails. Unused wallpapers will be deleted by the backend.
    }
  }, [clearSelectedWallpaper, uploadedCustomWallpaper?.assetId]);

  const selectWallpaper = useCallback(
    (localId: string) => {
      const wallpaper = wallpapers.find(
        (wallpaper) => wallpaper.localId === localId
      );

      if (!wallpaper) {
        throw new Error(`Wallpaper not found: ${localId}`);
      }
      setSelectedWallpaper(wallpaper);
      displayWallpaper(
        getWallpaperSrc(wallpaper),
        wallpaperLink,
        wallpaperTitle
      );
    },
    [wallpapers, wallpaperLink, wallpaperTitle, setSelectedWallpaper]
  );

  const { getState } = useStore<State>();

  const uploadCustomWallpaper = useCallback(
    async (file: File) => {
      if (!(await validateWallpaperFile(file))) {
        // eslint-disable-next-line no-alert
        alert(INVALID_FILE_ERROR_MESSAGE);

        return;
      }

      let blurredPreviewSrc: string | undefined;

      try {
        const localFileSrc = URL.createObjectURL(file);
        blurredPreviewSrc = await blurImageUrl(localFileSrc, 50);
        URL.revokeObjectURL(localFileSrc);
      } catch {
        // Fail silently
      }

      // Preview local file first
      const localWallpaper: Wallpaper = {
        localId: `custom_${file.name}`,
        category: WallpaperCategory.CUSTOM,
        localFileSrc: blurredPreviewSrc,
        fileName: file.name,
        extension:
          Helper.getFileExtension(file.name)?.toUpperCase() || 'UNKNOWN',
      };

      setUploadedCustomWallpaper(localWallpaper);
      addPendingWallpaperId(localWallpaper.localId);
      setSelectedWallpaper(localWallpaper);

      displayWallpaper(
        getWallpaperSrc(localWallpaper),
        wallpaperLink,
        wallpaperTitle
      );

      try {
        const { assetId } = await uploadCustomWallpaperFile(file);

        // First mark this upload as completed in case the transfer is sent before previews are complete
        addCompletedWallpaperAssetId(localWallpaper.localId, assetId);

        const { resizedSrcLarge, resizedSrcMedium, resizedSrcSmall } =
          await getWallpaperPreviews(assetId);

        const wallpaper: Wallpaper = {
          ...localWallpaper,
          assetId,
          localFileSrc: undefined,
          srcSmall: resizedSrcSmall,
          srcMedium: resizedSrcMedium,
          srcLarge: resizedSrcLarge,
        };

        /**
         * If user has not selected a different wallpaper during upload, select the new wallpaper.
         * The selectedWallpaper state may be out of date by now so we need to reselect it.
         **/
        const freshSelectedWallpaper = selectCustomWallpaper(getState());

        if (freshSelectedWallpaper) {
          setUploadedCustomWallpaper(wallpaper);

          if (freshSelectedWallpaper?.localId === wallpaper.localId) {
            setSelectedWallpaper(wallpaper);
            displayWallpaper(
              getWallpaperSrc(wallpaper),
              wallpaperLink,
              wallpaperTitle
            );
          }
        } else {
          // If selection has been reset, clean up uploaded wallpaper
          // Cancel upload
          try {
            await deleteWallpaper(wallpaper.assetId);
          } catch {
            // It's okay if this fails. Unused wallpapers will be deleted by the backend.
          }
        }
      } catch {
        clearCompletedWallpaperAssetIds();
        setUploadedCustomWallpaper(null);
        clearSelectedWallpaper();
        deletePendingWallpaperId(localWallpaper.localId);
        // eslint-disable-next-line no-alert
        alert(GENERIC_ERROR_MESSAGE);
      }

      deletePendingWallpaperId(localWallpaper.localId);

      if (blurredPreviewSrc) {
        URL.revokeObjectURL(blurredPreviewSrc);
      }
    },
    [
      addCompletedWallpaperAssetId,
      addPendingWallpaperId,
      clearCompletedWallpaperAssetIds,
      clearSelectedWallpaper,
      deletePendingWallpaperId,
      getState,
      setSelectedWallpaper,
      wallpaperLink,
      wallpaperTitle,
    ]
  );

  const uploadExistingWallpaper = useCallback(
    async (localId: string) => {
      const wallpaper = wallpapers.find(
        (wallpaper) => wallpaper.localId === localId
      );

      try {
        if (!wallpaper) {
          throw new Error(`Wallpaper not found: ${localId}`);
        }

        if (wallpaper.category === WallpaperCategory.CUSTOM) {
          throw new Error(
            `Invalid wallpaper category: ${WallpaperCategory.CUSTOM}`
          );
        }

        setSelectedWallpaper(wallpaper);

        displayWallpaper(
          getWallpaperSrc(wallpaper),
          wallpaperLink,
          wallpaperTitle
        );

        addPendingWallpaperId(localId);

        const { assetId } =
          wallpaper.category === WallpaperCategory.CURATED
            ? await uploadCuratedWallpaper(wallpaper.originalAssetUrl)
            : await uploadBrandedWallpaper(wallpaper.originalAssetUrl);

        if (!assetId) {
          throw new Error('assetId is not set');
        }

        // First mark this upload as completed in case the transfer is sent before previews are complete
        addCompletedWallpaperAssetId(localId, assetId);

        // Generate previews
        await getWallpaperPreviews(assetId);

        /**
         * If user has not selected a different wallpaper during upload, select the new wallpaper.
         * The selectedWallpaper state may be out of date by now so we need to reselect it.
         **/
        const freshSelectedWallpaper = selectCustomWallpaper(getState());

        if (freshSelectedWallpaper?.localId === wallpaper.localId) {
          setSelectedWallpaper(wallpaper);
          displayWallpaper(
            getWallpaperSrc(wallpaper),
            wallpaperLink,
            wallpaperTitle
          );
        }
      } catch {
        clearCompletedWallpaperAssetIds();
        clearSelectedWallpaper();
        // eslint-disable-next-line no-alert
        alert(GENERIC_ERROR_MESSAGE);
      }

      deletePendingWallpaperId(localId);
    },
    [
      addCompletedWallpaperAssetId,
      addPendingWallpaperId,
      clearCompletedWallpaperAssetIds,
      clearSelectedWallpaper,
      deletePendingWallpaperId,
      getState,
      setSelectedWallpaper,
      wallpaperLink,
      wallpaperTitle,
      wallpapers,
    ]
  );

  const value: TransferCustomizationContextValue = useMemo(
    () => ({
      clearUploadedWallpaper,
      closeDrawer,
      completedWallpaperAssetIds,
      isDrawerOpen,
      isEnabledForSender,
      isUploadInProgress,
      openDrawer,
      pendingWallpaperIds,
      selectedWallpaperId: selectedWallpaper?.localId,
      selectWallpaper,
      uploadCustomWallpaper,
      uploadedWallpaperId: uploadedCustomWallpaper?.localId || null,
      uploadExistingWallpaper,
      wallpapers,
    }),
    [
      clearUploadedWallpaper,
      closeDrawer,
      completedWallpaperAssetIds,
      isDrawerOpen,
      isEnabledForSender,
      isUploadInProgress,
      openDrawer,
      pendingWallpaperIds,
      selectedWallpaper?.localId,
      selectWallpaper,
      uploadCustomWallpaper,
      uploadedCustomWallpaper?.localId,
      uploadExistingWallpaper,
      wallpapers,
    ]
  );

  return (
    <TransferCustomizationContext.Provider value={value}>
      {children}
    </TransferCustomizationContext.Provider>
  );
};

TransferCustomizationProvider.displayName = 'TransferCustomizationProvider';
